import { z } from 'zod';
import { Id, IsoDate, PageQuerySchema } from './common';
import { ActorType, JobStatus } from './enums';
/* ============================================================================
   System: health, version/releases, jobs, uploads, audit
   ========================================================================== */
export const HealthSchema = z.object({
    status: z.literal('ok'),
    uptimeSeconds: z.number(),
    db: z.enum(['up', 'down', 'skipped']),
    time: z.string(),
});
/** Written to /version.json at build time and served by GET /v1/version. */
export const VersionSchema = z.object({
    version: z.string(),
    build: z.string(),
    notes: z.array(z.string()).default([]),
});
export const ReleaseSchema = z.object({
    id: Id,
    version: z.string(),
    codename: z.string().nullable(),
    notes: z.array(z.string()),
    publishedAt: IsoDate.nullable(),
    publishedBy: z.string().nullable(),
    createdAt: IsoDate,
});
export const ReleaseCreateSchema = z.object({
    version: z.string().regex(/^\d+\.\d+\.\d+$/),
    codename: z.string().max(40).optional(),
    notes: z.array(z.string().min(1).max(200)).min(1).max(10),
});
/* ---------- Jobs (PLAN §12) ---------- */
export const JOB_NAMES = [
    'subscription-lifecycle',
    'subscription-expiry-reminders',
    'offer-links-expiry',
    'invitation-expiry',
    'auth-token-cleanup',
    'account-deletion-purge',
    'tenant-deletion-purge',
    'domain-verification',
    'domain-live-notify',
    'due-date-reminders',
    'daily-digest',
    'email-outbox-retry',
    'push-delivery-retry',
    'usage-snapshot',
    'data-export-builder',
    'db-backup',
    'notifications-cleanup',
    'audit-log-retention',
];
export const JobRunSchema = z.object({
    id: Id,
    job: z.string(),
    status: z.enum(JobStatus),
    startedAt: IsoDate,
    finishedAt: IsoDate.nullable(),
    durationMs: z.number().int().nullable(),
    error: z.string().nullable(),
});
export const JobSchema = z.object({
    name: z.string(),
    schedule: z.string(),
    description: z.string(),
    lastRun: JobRunSchema.nullable(),
    nextRunAt: IsoDate.nullable(),
});
export const JobTickResultSchema = z.object({
    ran: z.array(z.object({ job: z.string(), status: z.enum(JobStatus), durationMs: z.number().int() })),
    skipped: z.array(z.string()),
});
/* ---------- Uploads (R2 presigned PUT, PLAN INFRA) ---------- */
export const UploadPurpose = ['attachment', 'avatar', 'logo', 'logo_dark', 'icon', 'offer_logo'];
export const PresignRequestSchema = z.object({
    purpose: z.enum(UploadPurpose),
    fileName: z.string().min(1).max(255),
    mimeType: z.string().min(3).max(120),
    sizeBytes: z.number().int().positive(),
});
export const PresignResponseSchema = z.object({
    uploadId: Id,
    method: z.literal('PUT'),
    url: z.string().url(),
    headers: z.record(z.string()),
    expiresAt: IsoDate,
    /** public URL for public purposes (logos), null for private attachments */
    publicUrl: z.string().url().nullable(),
});
export const UploadCompleteSchema = z.object({ uploadId: Id });
/* ---------- Audit log ---------- */
export const AuditEntrySchema = z.object({
    id: Id,
    actorType: z.enum(ActorType),
    actor: z.object({ id: Id.nullable(), name: z.string(), email: z.string().nullable() }),
    tenant: z.object({ id: Id, name: z.string() }).nullable(),
    action: z.string(),
    target: z.object({ type: z.string().nullable(), id: z.string().nullable(), label: z.string().nullable() }),
    ip: z.string().nullable(),
    data: z.record(z.unknown()).nullable(),
    createdAt: IsoDate,
});
export const AuditQuery = PageQuerySchema.extend({
    tenantId: Id.optional(),
    actorId: Id.optional(),
    action: z.string().optional(),
    from: IsoDate.optional(),
    to: IsoDate.optional(),
});
//# sourceMappingURL=system.js.map